import { Stack, Stepper, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconCheckbox,
  IconPointFilled,
  IconReportAnalytics,
  IconSpeakerphone,
  IconUsers,
} from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Timer from '@/components/Timer'
import { testAllElectionPhases } from '@/constants/blockchain'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { ElectionData, mock } from '@/hooks/useGovernance'

import GovInfo from '../GovInfo'
import Campaign from './Campaign'
import Nomination from './Nomination'
import Results from './Results'
import Voting from './Voting'

const ELECTION_PHASES = ['Nomination', 'Campaign', 'Voting', 'Results']
// const currentPhase = ELECTION_PHASES[1]

interface StepProps {
  children: React.ReactNode
  step: number
  active: number
  currentPhase: string
  onStepClick: (step: number) => void
}

const timeToPhase = (now: number, electionTime: ElectionData): string => {
  const { idx, nominationTime, campaignTime, votingTime, endTime } = electionTime
  const [NOMINATION, CAMPAIGN, VOTING, RESULTS] = ELECTION_PHASES
  let electionPhase = NOMINATION
  if (idx === 0 || now < nominationTime) {
    // Not started (reuse nominate for now)
    electionPhase = NOMINATION
  } else if (now < campaignTime) {
    // Nomination
    electionPhase = NOMINATION
  } else if (now < votingTime) {
    // Campaign
    electionPhase = CAMPAIGN
  } else if (now < endTime) {
    // Voting
    electionPhase = VOTING
  } else {
    // Results
    electionPhase = RESULTS
  }
  return electionPhase
}

const Step: React.FC<StepProps> = ({ children, step, active, onStepClick, currentPhase }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const icons = [
    <IconUsers key="icon1" size={18} />,
    <IconSpeakerphone key="icon2" size={18} />,
    <IconCheckbox key="icon3" size={18} />,
    <IconReportAnalytics key="icon4" size={18} />,
  ]

  const labelColor = useMemo(
    () => () =>
      active > step
        ? theme.colors.l1Primary[0]
        : ELECTION_PHASES.indexOf(currentPhase) < step
          ? theme.colors.textAndIcon[0]
          : theme.colors.title[0],
    [currentPhase, active]
  )

  const borderColor = useMemo(
    () => () =>
      active >= step
        ? theme.colors.l1Primary[0]
        : ELECTION_PHASES.indexOf(currentPhase) < step
          ? theme.colors.textAndIcon[0]
          : theme.colors.title[0],
    [currentPhase, active]
  )

  return (
    <Stack
      style={{ position: 'relative', alignItems: 'center', cursor: 'pointer' }}
      onClick={() => onStepClick(step)}
    >
      <Stepper.Step
        icon={icons[step]}
        completedIcon={icons[step]}
        style={{
          cursor: ELECTION_PHASES.indexOf(currentPhase) < 0 ? 'not-allowed' : 'pointer',
          padding: isMobile ? '0 15px' : '0 30px',
        }}
        styles={{
          stepLabel: {
            color: labelColor(),
          },
          stepIcon: {
            color: labelColor(),
            borderColor: borderColor(),
          },
        }}
        onClick={() => onStepClick(step)}
      >
        {children}
      </Stepper.Step>
      <Stack
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          gap: '5px',
          position: 'absolute',
          bottom: '-45px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Title order={5} style={{ color: labelColor(), fontSize: isMobile ? '12px' : '' }}>
          {ELECTION_PHASES[step]}
        </Title>
        <Tooltip
          label={t('components.governance.votingTooltip', 'Current Election Phase')}
          position="bottom"
          style={{ display: ELECTION_PHASES.indexOf(currentPhase) === step ? 'block' : 'none' }}
        >
          <IconPointFilled
            size={14}
            color={
              ELECTION_PHASES.indexOf(currentPhase) === step
                ? theme.colors.l1Primary[0]
                : theme.colors.background[0]
            }
          />
        </Tooltip>
      </Stack>
    </Stack>
  )
}

const GovernanceStepper: React.FC = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const now = useMemo(() => Math.floor(Date.now() / 1000), [])

  // Governance data
  const { electionData } = useGovernanceContext()
  // mock election time
  const startTimeRef = useRef(now)
  const mockElectionTime = useMemo(() => {
    const nominationSeconds = 5
    const campaignSeconds = 5
    const votingSeconds = 5
    const endSeconds = 100000
    const timeToStart = startTimeRef.current

    return {
      idx: 1,
      nominationTime: timeToStart + nominationSeconds,
      campaignTime: timeToStart + nominationSeconds + campaignSeconds,
      votingTime: timeToStart + nominationSeconds + campaignSeconds + votingSeconds,
      endTime: timeToStart + nominationSeconds + campaignSeconds + votingSeconds + endSeconds,
    }
  }, [startTimeRef])
  const electionTime = useMemo(() => (mock ? mockElectionTime : electionData), [electionData, mock])

  const initialPhase = useMemo(
    () => (testAllElectionPhases ? ELECTION_PHASES.length - 1 : 0),
    [testAllElectionPhases]
  )
  const [currentPhase, setCurrentPhase] = useState(ELECTION_PHASES[initialPhase])
  const [active, setActive] = useState(0)
  const [changePhase, setChangePhase] = useState(false)

  // Timer timeout
  const timeout = useCallback(() => {
    setTimeout(() => {
      setChangePhase(prev => !prev)
    }, 500) // process change 500ms after to allow time to be after the required expiry
  }, [])

  const handleStepClick = useCallback(
    (step: number) => {
      if (step <= ELECTION_PHASES.indexOf(currentPhase)) {
        setActive(step)
      }
    },
    [currentPhase]
  )

  // Times for election stages
  const expiryTimestamp = useMemo(() => {
    const { nominationTime, campaignTime, votingTime, endTime } = electionTime
    switch (currentPhase) {
      case ELECTION_PHASES[0]:
        // On nomination need to return nominationTime if higher than current time
        return Math.floor(Date.now() / 1000) < nominationTime ? nominationTime : campaignTime
      case ELECTION_PHASES[1]:
        return votingTime
      case ELECTION_PHASES[2]:
        return endTime
      default:
        return 0
    }
  }, [currentPhase, electionTime, changePhase])

  useEffect(() => {
    if (testAllElectionPhases) return
    const electionPhase = timeToPhase(Math.floor(Date.now() / 1000), electionTime)
    setCurrentPhase(electionPhase)
    setActive(ELECTION_PHASES.indexOf(electionPhase))
  }, [electionTime, changePhase, testAllElectionPhases])

  return (
    <>
      <Stack
        style={{
          justifyContent: 'flex-end',
          flexDirection: 'row',
        }}
      >
        <Timer
          expiryTimestamp={expiryTimestamp}
          onTimeout={timeout}
          text={
            currentPhase === 'Results'
              ? t('components.governance.timerTextElectionstep', 'Next election in:')
              : t('components.governance.timerTextstep', 'Next phase in:')
          }
          secondary
        />
      </Stack>
      <Stepper
        size={isMobile ? 'xs' : 'sm'}
        active={active}
        onStepClick={handleStepClick}
        color={theme.colors.l1Primary[0]}
        iconSize={isMobile ? 27 : 32}
        styles={{
          step: {
            padding: '5px',
            flexFlow: 'column',
          },
          steps: {
            marginBottom: '50px',
          },
          stepBody: {
            padding: '10px',
            margin: '0',
          },
          stepDescription: {
            alignSelf: 'center',
          },
          stepLabel: {
            alignSelf: 'center',
            fontSize: isMobile ? '11px' : '14px',
          },
          stepIcon: {
            borderWidth: 4,
            color: theme.colors.title[0],
          },

          separator: {
            marginLeft: -2,
            marginRight: -2,
            height: 5,
          },
          content: {
            paddingTop: '5px',
          },
        }}
      >
        <Step step={0} active={active} onStepClick={handleStepClick} currentPhase={currentPhase}>
          <Stack gap="30px">
            <GovInfo />
            <Nomination />
          </Stack>
        </Step>
        <Step step={1} active={active} onStepClick={handleStepClick} currentPhase={currentPhase}>
          <Stack gap="30px">
            <GovInfo />
            <Campaign />
          </Stack>
        </Step>
        <Step step={2} active={active} onStepClick={handleStepClick} currentPhase={currentPhase}>
          <Stack gap="30px">
            <GovInfo />
            <Voting />
          </Stack>
        </Step>
        <Step step={3} active={active} onStepClick={handleStepClick} currentPhase={currentPhase}>
          <Stack gap="30px">
            <GovInfo />
            <Results />
          </Stack>
        </Step>
      </Stepper>
    </>
  )
}

export default GovernanceStepper
